import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { set } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '@components/ui/calendar';
import { FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { SelectInput } from '@components/ui/form/select/input';
import { PhoneTimeRange } from '@components/scheduling/phoneTimeRange';
import { TimeRange } from '@components/scheduling/timeRangePicker/types';
import { useDropdownsContext } from '@contexts/dropdowns';
import { toLocalWallClock, toTenantDate } from '@utils/timeZone';
import { numericDate } from '@utils/i18n';
import { getFullName } from '@utils/textHelpers';
import { cn } from '@utils/cn';
import { RelatedInternalQuickEditProps } from './types';

const toMinutes = (iso: string) => {
  const date = toTenantDate(iso);
  return date.getHours() * 60 + date.getMinutes();
};

const withMinutes = (iso: string, minutes: number) =>
  set(toTenantDate(iso), { hours: Math.floor(minutes / 60), minutes: minutes % 60 }).toISOString();

const withDay = (iso: string, day: Date) =>
  set(toTenantDate(iso), {
    year: day.getFullYear(),
    month: day.getMonth(),
    date: day.getDate(),
  }).toISOString();

export const RelatedInternalQuickEdit = ({ data, disabled }: RelatedInternalQuickEditProps) => {
  const { t } = useTranslation('app');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { fieldUsers, setParams } = useDropdownsContext();
  const { control, watch, setValue } = useFormContext<{
    startsAt: string;
    endsAt: string;
    relatedScheduleForemanId: string;
  }>();
  const startsAt = watch('startsAt');
  const endsAt = watch('endsAt');
  const specialty = data.task?.foremanSpecialty?.value || '';
  const foremanId = data.foreman?.id;

  useEffect(() => {
    setParams({ users: { specialty: [specialty] } });
    return () => setParams({ users: {} });
  }, [setParams, specialty]);

  const options = useMemo(() => {
    const list = fieldUsers.options.map((el) => ({
      name: el.fullName || el.name || '',
      value: el.id,
    }));
    if (foremanId && !list.some((el) => el.value === foremanId))
      return [
        {
          name:
            data.foreman?.fullName || getFullName(data.foreman?.firstName, data.foreman?.lastName),
          value: foremanId,
        },
        ...list,
      ];
    return list;
  }, [fieldUsers.options, foremanId, data.foreman]);

  if (!startsAt || !endsAt) return null;

  const range: TimeRange = { start: toMinutes(startsAt), end: toMinutes(endsAt) };
  const day = toLocalWallClock(toTenantDate(startsAt));

  return (
    <>
      <div className="flex flex-col gap-1">
        <span className="text-sm text-neutral-600">{t('forms.date.label')}*</span>
        <button
          type="button"
          disabled={disabled}
          aria-expanded={calendarOpen}
          onClick={() => setCalendarOpen(!calendarOpen)}
          className={cn(
            'flex h-11 w-full items-center gap-2 rounded-md border-[1.5px] border-neutral-300 bg-white pl-3 pr-2.5 text-left text-base disabled:bg-neutral-50',
            calendarOpen && 'border-2 border-primary-500',
          )}>
          <span className="min-w-0 flex-1 truncate">{numericDate(day)}</span>
          <CalendarDays className="h-5 w-5 shrink-0 text-neutral-500" />
        </button>
        {calendarOpen && (
          <div className="flex justify-center rounded-xl border border-neutral-200 bg-white [&>div]:max-h-none">
            <Calendar
              mode="single"
              selected={day}
              defaultMonth={day}
              weekStartsOn={1}
              onSelect={(value) => {
                if (!value) return;
                setCalendarOpen(false);
                setValue('startsAt', withDay(startsAt, value), { shouldDirty: true });
                setValue('endsAt', withDay(endsAt, value), { shouldDirty: true });
              }}
              classNames={{
                weekday: 'w-10 text-[0.8rem] font-normal text-neutral-500',
                day_button: 'h-10 w-10 rounded-full p-0 hover:bg-primary-50',
                selected: 'rounded-full bg-primary-500 text-white',
              }}
            />
          </div>
        )}
      </div>
      <PhoneTimeRange
        value={range}
        disabled={disabled}
        onChange={(value) => {
          setValue('startsAt', withMinutes(startsAt, value.start), { shouldDirty: true });
          setValue('endsAt', withMinutes(endsAt, value.end), { shouldDirty: true });
        }}
      />
      <FormField
        control={control}
        name="relatedScheduleForemanId"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('scheduling.phone.fieldUser')} select>
            <SelectInput
              name="relatedScheduleForemanId"
              value={value}
              onChange={(next) => next && onChange(next)}
              options={options}
              sheetTitle={t('scheduling.phone.fieldUser')}
              disabled={disabled || fieldUsers.disabled}
              placeholder={t('scheduling.phone.unassigned')}
            />
          </FormItemWrapper>
        )}
      />
    </>
  );
};
